import { Button } from "@/components/ui/button";
import { uploadVisitData } from "@/lib/nut/NutAPI";
import { sendChatMessage } from "@/lib/nut/SendChatMessage";
import { ChatMode, NutChatRequest } from "@/lib/nut/SendChatMessage";
import type { Message } from "@/lib/nut/Message";
import type { ChatResponse } from "@/lib/nut/Response";
import { getSimulationDataDirect } from "@/messages/handler";
import { Loader2, Send, X } from "lucide-react";
import { useState, useEffect } from "react";

const USER_RESPONSE_CATEGORY = 'UserResponse';

function generateRandomId() {
  return Math.random().toString(16).substring(2, 10);
}

async function sendMessage(message: string, onResponse: (response: ChatResponse) => void) {
  const repositoryId = import.meta.env.VITE_REPOSITORY_ID;
  const simulationData = await getSimulationDataDirect();

  const visitDataId = await uploadVisitData({ repositoryId, simulationData });

  const apiMessage: Message = {
    id: `user-inline-${generateRandomId()}`,
    role: "user",
    content: message,
    createTime: new Date().toISOString(),
  };

  const request: NutChatRequest = {
    appId: import.meta.env.VITE_APP_ID,
    mode: ChatMode.InlineAppUserMessage,
    messages: [apiMessage],
    visitDataId,
  };

  await sendChatMessage(request, onResponse);
}

interface NutMessageOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NutMessageOverlay({ isOpen, onClose }: NutMessageOverlayProps) {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [assistantResponse, setAssistantResponse] = useState<{ id: string; content: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasSentMessage, setHasSentMessage] = useState(false);

  const appId = import.meta.env.VITE_APP_ID;

  // Clear message when overlay closes
  useEffect(() => {
    if (!isOpen) {
      setMessage("");
      setIsSending(false);
      setAssistantResponse(null);
      setHasSentMessage(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!message.trim() || isSending) {
      return;
    }

    setIsSending(true);
    setAssistantResponse(null);
    setErrorMessage(null);
    setHasSentMessage(false);

    try {
      await sendMessage(message, response => {
        switch (response.kind) {
          case "message":
            if (
              response.message.role === "assistant" &&
              response.message.category === USER_RESPONSE_CATEGORY
            ) {
              setAssistantResponse(prev => {
                const previousContent = prev && prev.id === response.message.id ? prev.content : "";
                return {
                  id: response.message.id,
                  content: previousContent + response.message.content,
                };
              });
            }
            break;
          case "error":
            setErrorMessage(typeof response.error === "string" ? response.error : "An error occurred while sending the message.");
            break;
        }
      });
      setHasSentMessage(true);
    } catch (error) {
      console.error("Failed to send Nut message", error);
      setErrorMessage(error instanceof Error ? error.message : "Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end p-4"
      onClick={() => {
        if (!isSending) {
          onClose();
        }
      }}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg p-4 w-80 mt-16 mr-4 flex max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col w-full">
          <div className="flex items-start justify-between mb-4 gap-3">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold">Report Bug</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Describe a problem you're seeing or an improvement to make.
              </p>
            </div>
            <Button
              onClick={() => {
                if (!isSending) {
                  onClose();
                }
              }}
              className="p-1 h-auto w-auto hover:bg-accent rounded"
              variant="ghost"
              disabled={isSending}
            >
              <X size={18} />
            </Button>
          </div>
          <div className="space-y-3 overflow-y-auto pr-1 flex-1">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              className="w-full min-h-[100px] p-2 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={isSending}
            />
            {assistantResponse && (
              <div className="border border-border rounded-md p-2 text-sm space-y-1">
                <div className="font-medium text-foreground">Response</div>
                <div className="text-muted-foreground whitespace-pre-wrap">{assistantResponse.content}</div>
              </div>
            )}
            {errorMessage && (
              <div className="border border-destructive/50 bg-destructive/10 text-destructive rounded-md p-2 text-sm">
                {errorMessage}
              </div>
            )}
          </div>
          {!hasSentMessage ? (
            <Button
              onClick={handleSendMessage}
              className="w-full flex items-center justify-center gap-2 mt-4"
              disabled={!message.trim() || isSending}
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send
                </>
              )}
            </Button>
          ) : (
            <div className="mt-4 text-sm text-muted-foreground text-center space-y-1">
              <p>Nut will work on fixing the issue. You may close this window.</p>
              <a
                href={`https://nut.new/app/${appId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Open in Nut
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NutMessageOverlay;
